import { action, runInAction } from 'mobx';

import Agent from 'env/agent';
import { getStores } from './stores';
import { lowerFirstLetter } from 'env/utils/strings';
import { analyticsService } from 'env/analytics/analyticsService';

const loadDataHelper = async ({
  store,
  agentKey,
  key,
  path,
  params,
  onDataReceived,
  headers,
  // Optional override for how the data is fetched, keeping the loading/loaded
  // bookkeeping below. Used to route a load through a cache without having to
  // hand-roll the rest of the helper.
  fetcher,
}) => {
  var agent = new Agent(store.host, store.authTokens);
  store[`isLoading${key}Lock`] = true; //this prop is not observable, no need to wrap in action
  runInAction(() => {
    if (params) store[`hasLoaded${key}`] = false;
    store[`isLoading${key}`] = true;
  });

  try {
    const result = fetcher
      ? await fetcher()
      : await agent[agentKey][`get${key}`](params, headers);

    if (onDataReceived) onDataReceived(result);

    const returnData = path ? result[path] : result;
    runInAction(() => {
      if (!onDataReceived) store[lowerFirstLetter(key)] = returnData;
    });
    return returnData;
  } finally {
    runInAction(() => {
      store[`isLoading${key}`] = false;
      store[`hasLoaded${key}`] = true;
      store[`isLoading${key}Lock`] = false;
    });
  }
};

const saveDateHelper = ({
  store,
  agentKey,
  key,
  data,
  validation,
  validationSchema,
  modelToValidate,
}) => {
  var agent = new Agent(store.host, store.authTokens);
  if (validation) {
    const validationKey = `${lowerFirstLetter(key)}Validation`;
    const toValidate = modelToValidate || store[lowerFirstLetter(key)];
    store[validationKey] = validation
      .reset()
      .validate(toValidate, validationSchema);
    if (!store[validationKey].isValid) {
      // Custom errors => string logic
      // seperate mutiple errors per field, remove dup errors.
      const allErrors = Object.values(store[validationKey].errors)
        .flat(3)
        .map((e) => `- ${e}`);
      const uniqueErrors = [...new Set(allErrors)].join('<br/>');
      return Promise.reject(uniqueErrors);
    }
  }

  //Prevent the same request being fired again while on for the same store and is running
  if (store[`isSaving${key}Lock`] === true) return Promise.reject();
  store[`isSaving${key}Lock`] = true; //this prop is not observable, no need to wrap in action

  store[`isSaving${key}`] = true;
  var dataToSubmit = data || store[lowerFirstLetter(key)];
  return agent[agentKey][`save${key}`](dataToSubmit)
    .then(
      action((data) => {
        if (!data) return data;
        if (data.ErrorMessage) return Promise.reject(data.ErrorMessage);
        return data;
      })
    )
    .finally(
      action((data) => {
        const stores = getStores();
        if (stores) {
          analyticsService.saved({
            agentKey,
            key,
            ...stores.authStore.customer,
          });

          store[`isSaving${key}`] = false;
          store[`isSaving${key}Lock`] = false;
          return data;
        }
      })
    );
};

export { saveDateHelper, loadDataHelper };
